import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';
import { CopyDocumentType, CopyMode } from './document-copy-recipient.entity';

/**
 * PD-15003 — a record of who was copied on which document, and how.
 *
 * Exists for two reasons, both of which the alternative (reading it back off
 * `email_record`) cannot serve: that table has no link to an invoice at all —
 * its only foreign keys are email_source, email_template and
 * automation_step_execution.
 *
 *   1. **Suppression.** A delivery receipt already carries the invoice PDF, so
 *      someone copied on the receipt has already received that invoice.
 *      If a person later emails the same invoice by hand, anyone already
 *      copied for that invoice is skipped rather than sent a second copy of a
 *      document they have. Scoped to the invoice — being copied on invoice 2
 *      must never suppress a copy of invoice 3.
 *
 *   2. **Traceability of blind copies.** A BCC that leaves no trace of who
 *      received it is very hard to support afterwards ("who else saw this?").
 *
 * Written after the send succeeds, so a failed send does not suppress a later
 * retry.
 */
@Index('idx_document_copy_send_invoice', ['fkInvoiceId', 'emailAddress'], {})
@Entity('document_copy_send')
export class DocumentCopySendEntity extends BaseEntity {
	@Column('varchar', { name: 'documentType', length: 40 })
	public documentType!: CopyDocumentType;

	@Column('varchar', { name: 'copyMode', length: 10 })
	public copyMode!: CopyMode;

	/**
	 * The invoice this copy conveyed. The delivery receipt attaches one, which
	 * is what lets a receipt suppress a later invoice copy. Null for a document
	 * with no invoice behind it.
	 */
	@Column('int', { name: 'fk_invoice__id', unsigned: true, nullable: true })
	public fkInvoiceId!: number | null;

	/** Lower-cased at write time so suppression matching is case-insensitive. */
	@Column('varchar', { name: 'emailAddress', length: 255 })
	public emailAddress!: string;
}
